'use client'

import Image from "next/image";
import Link from "@/components/ui/Link";
import { sanitizeHTML } from "@/lib/utils";
import { getReadableImageUrl } from "@/lib/directus";
import { useEffect, useRef, useState } from "react";

interface HomeAwardsData {
  heading?: {
    text?: string;
    title?: string;
  };
  subheading?: {
    text?: string;
    title?: string;
  };
  text?: {
    text?: string;
  };
  image?: {
    alt?: string;
    image_file?: {
      image?: {
        id?: string;
        filename_download?: string;
      };
    };
  };
  button?: {
    label?: string;
    href?: string;
  };
  images?: {
    images_id?: {
      alt?: string;
      image_file?: {
        image?: {
          id?: string;
          filename_download?: string;
        };
      };
    };
  }[];
}

export const fields = [
  "heading.text",
  "heading.title",
  "subheading.text",
  "subheading.title",
  "text.text",
  "image.alt",
  "image.image_file.image.id",
  "image.image_file.image.filename_download",
  "button.label",
  "button.href",
  "images.images_id.alt",
  "images.images_id.image_file.image.id",
  "images.images_id.image_file.image.filename_download"
] as const;

export default function HomeAwards({ data }: { data?: HomeAwardsData }) {
  const sectionRef = useRef<HTMLElement>(null);
  const logosRef = useRef<HTMLDivElement>(null);
  const [sectionVisible, setSectionVisible] = useState(false);
  const [logosVisible, setLogosVisible] = useState(false);

  useEffect(() => {
    const sectionObserver = new IntersectionObserver(
      ([entry]) => setSectionVisible(entry.isIntersecting),
      { threshold: 0.08, rootMargin: "0px" }
    );
    const sectionCurrent = sectionRef.current;
    if (sectionCurrent) sectionObserver.observe(sectionCurrent);

    const observer = new IntersectionObserver(
      ([entry]) => setLogosVisible(entry.isIntersecting),
      { threshold: 0.15, rootMargin: "-50px" }
    );
    const current = logosRef.current;
    if (current) observer.observe(current);
    return () => {
      if (current) observer.unobserve(current);
      if (sectionCurrent) sectionObserver.unobserve(sectionCurrent);
    };
  }, []);

  const imageUrl = getReadableImageUrl(
    data?.image?.image_file?.image?.id || "",
    data?.image?.image_file?.image?.filename_download || ""
  );

  return (
		<section ref={sectionRef} className='HomeAwards pt-16 md:py-16'>
			<div className='container'>
				{(data?.subheading || data?.heading) && (
					<div
						className={`text-center mb-8 md:mb-12 transition-all duration-700 ease-out ${
							sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
						}`}
					>
						{data?.subheading && (
							<div className='font-aileron font-bold text-sm uppercase tracking-wider text-secondary-dark mb-3'>
								{data.subheading.title || data.subheading.text}
							</div>
						)}
						{data?.heading && (
							<h2 className='mb-0'>
								{data.heading.title || data.heading.text}
							</h2>
						)}
					</div>
				)}

				<div className='flex flex-col lg:flex-row lg:space-x-8 mx-auto justify-center md:gap-16 lg:items-stretch'>
					<div
						className={`order-1 lg:order-1 w-full lg:w-1/2 mb-8 lg:mb-0 transition-all duration-700 ease-out ${
							sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
						}`}
						style={{ transitionDelay: sectionVisible ? '80ms' : '0ms' }}
					>
						{data?.image && imageUrl && (
							<div className='w-full overflow-hidden'>
								<Image
									src={imageUrl}
									alt={data.image.alt || ''}
									width={980}
									height={653}
									quality={95}
									loading='lazy'
									className='w-full h-auto object-cover'
									sizes='(min-width: 1024px) 50vw, 100vw'
								/>
							</div>
						)}
					</div>
					<div
						className={`order-2 lg:order-2 w-full lg:w-1/2 mb-8 lg:mb-0 relative flex flex-col justify-start transition-all duration-700 ease-out ${
							sectionVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
						}`}
						style={{ transitionDelay: sectionVisible ? '180ms' : '0ms' }}
					>
						<div>
							{data?.text && (
								<div
									className='grid gap-6'
									dangerouslySetInnerHTML={sanitizeHTML(data.text.text || '')}
								/>
							)}
						</div>
						<div className='w-full'>
							{data?.button?.href && (
								<div className='mt-6 text-left w-full'>
									<Link className='btn' href={data.button.href}>
										{data.button.label}
									</Link>
								</div>
							)}
						</div>
					</div>
				</div>
				{data?.images && data.images.length > 0 && (
					<div
						ref={logosRef}
						className='mt-8 md:mt-16 py-6 border-y border-[#cfcfcf] grid grid-cols-6 gap-x-4 gap-y-6 md:flex md:flex-wrap items-center justify-center md:justify-between md:gap-8 2xl:gap-6'
					>
						{data.images.map((logo, index) => {
							const logoSrc = getReadableImageUrl(
								logo?.images_id?.image_file?.image?.id || '',
								logo?.images_id?.image_file?.image?.filename_download || '',
							)
							return (
							<div
								key={index}
								className={`col-span-2 transition-all duration-700 ease-out grayscale hover:grayscale-0 flex items-center justify-center ${
									index === 3 ? 'col-start-2 md:col-start-auto' : ''
								} ${index === 4 ? 'col-start-4 md:col-start-auto' : ''} ${
									logosVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
								}`}
								style={{
									transitionDelay: logosVisible ? `${index * 100}ms` : '0ms',
								}}
							>
								{logoSrc && (
								<Image
									src={logoSrc}
									alt={logo?.images_id?.alt || ''}
									width={120}
									height={80}
									className='w-auto h-auto object-contain max-w-[100px] max-h-[70px] sm:max-w-[120px] sm:max-h-[80px] md:max-w-full md:max-h-none'
									loading='lazy'
									sizes='120px'
								/>
								)}
							</div>
							)
						})}
					</div>
				)}
			</div>
		</section>
	)
}
